// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ErrorCode } from '@/api/types';
import { Currency } from '@/currency/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import {
  exceedsMaxCurrencyAmount,
  getMinorUnitDigits,
  hasFractionalUnits,
  isZeroDecimal,
  parseCurrencyAmount,
} from '@/currency/utilities/validate';
import { Decimal } from 'decimal.js';

/** Error codes mirror the BE 3800-block currency error constants. */
type CurrencyErrorCode = Extract<
  ErrorCode,
  | 'currency_value_exceeds_maximum'
  | 'currency_mixed_iso'
  | 'currency_division_by_zero'
  | 'currency_zero_decimal_precision'
  | 'currency_invalid_amount'
>;

/** Thrown by currency math when an operation violates the BE currency rules. */
export class CurrencyError extends Error {
  readonly code: CurrencyErrorCode;

  constructor(message: string, code: CurrencyErrorCode) {
    super(message);
    this.name = 'CurrencyError';
    this.code = code;
  }
}

function toScalarDecimal(value: string): Decimal {
  const scalar = parseCurrencyAmount(value);
  if (scalar === null) {
    throw new CurrencyError(
      `Invalid scalar: ${value}`,
      'currency_invalid_amount'
    );
  }
  return scalar;
}

// Mirrors the BE `build` gate: every arithmetic result is re-validated
// against the NUMERIC(14,2) bound and the zero-decimal whole-unit rule.
function buildCurrency(
  amount: Decimal,
  currencyCode: IsoCurrencyCode
): Currency {
  if (exceedsMaxCurrencyAmount(amount)) {
    throw new CurrencyError(
      'Amount exceeds the maximum allowed value',
      'currency_value_exceeds_maximum'
    );
  }
  if (isZeroDecimal(currencyCode) && hasFractionalUnits(amount)) {
    throw new CurrencyError(
      `${currencyCode} amounts must be whole units`,
      'currency_zero_decimal_precision'
    );
  }
  return { amount, isoCode: currencyCode };
}

function requireSameIso(operation: string, a: Currency, b: Currency): void {
  if (a.isoCode !== b.isoCode) {
    throw new CurrencyError(
      `Cannot ${operation} ${a.isoCode} and ${b.isoCode}`,
      'currency_mixed_iso'
    );
  }
}

/** Sum of two same-ISO currencies; mixed-ISO operands throw. */
export function addCurrency(a: Currency, b: Currency): Currency {
  requireSameIso('add', a, b);
  return buildCurrency(a.amount.plus(b.amount), a.isoCode);
}

/** Difference of two same-ISO currencies; mixed-ISO operands throw. */
export function subCurrency(a: Currency, b: Currency): Currency {
  requireSameIso('subtract', a, b);
  return buildCurrency(a.amount.minus(b.amount), a.isoCode);
}

/**
 * Scales the amount by a dimensionless factor (e.g. tax rate, quantity).
 * The factor is a decimal string so callers never route money math through
 * IEEE-754 floats.
 */
export function mulCurrencyScalar(value: Currency, factor: string): Currency {
  return buildCurrency(
    value.amount.times(toScalarDecimal(factor)),
    value.isoCode
  );
}

/** Divides the amount by a non-zero dimensionless decimal-string divisor. */
export function divCurrencyScalar(value: Currency, divisor: string): Currency {
  const divisorDecimal = toScalarDecimal(divisor);
  if (divisorDecimal.isZero()) {
    throw new CurrencyError(
      'Cannot divide by zero',
      'currency_division_by_zero'
    );
  }
  return buildCurrency(value.amount.dividedBy(divisorDecimal), value.isoCode);
}

/** Value equality on amount and ISO code (10 equals 10.00). */
export function currencyEquals(a: Currency, b: Currency): boolean {
  return a.isoCode === b.isoCode && a.amount.equals(b.amount);
}

/** Converts an amount into a count of the currency's minor units (cents). */
export function toMinorUnits(
  amount: Decimal,
  currencyCode: IsoCurrencyCode
): Decimal {
  return amount.times(10 ** getMinorUnitDigits(currencyCode));
}

/** Converts a count of the currency's minor units back into an amount. */
export function fromMinorUnits(
  units: Decimal,
  currencyCode: IsoCurrencyCode
): Decimal {
  return units.dividedBy(10 ** getMinorUnitDigits(currencyCode));
}
